import {isSessionApprovalMode, type SessionApprovalMode} from '@github-ui/ahp-relay/permission-mode'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useCallback, useState, useSyncExternalStore} from 'react'

import {notifyStorageKeyChanged, subscribeToStorageKey} from '../task/same-tab-storage'

function getApprovalModeStorageKey(login: string): string {
  return `quick-chat:selected-approval-mode:${encodeURIComponent(login)}`
}

function readStoredApprovalModeAtKey(key: string): SessionApprovalMode | undefined {
  if (typeof window === 'undefined') return undefined
  try {
    const value = window.localStorage.getItem(key)
    return isSessionApprovalMode(value) ? value : undefined
  } catch {
    return undefined
  }
}

export function readStoredApprovalMode(login: string | undefined): SessionApprovalMode | undefined {
  if (!login) return undefined
  return readStoredApprovalModeAtKey(getApprovalModeStorageKey(login))
}

export function writeStoredApprovalMode(login: string | undefined, mode: SessionApprovalMode): void {
  if (typeof window === 'undefined' || !login) return
  const key = getApprovalModeStorageKey(login)
  try {
    window.localStorage.setItem(key, mode)
    notifyStorageKeyChanged(key)
  } catch {
    return
  }
}

function getStoredApprovalModeServerSnapshot(): undefined {
  return undefined
}

export function useNewSessionApprovalMode() {
  const enabled = isFeatureEnabled('one_copilot_remember_tool_permissions')
  const login = getCurrentUserLogin()
  const key = login ? getApprovalModeStorageKey(login) : undefined
  const subscribe = useCallback(
    (callback: () => void) => {
      if (!enabled || !key) return () => {}
      return subscribeToStorageKey(key, callback)
    },
    [enabled, key],
  )
  const getSnapshot = useCallback(() => (enabled && key ? readStoredApprovalModeAtKey(key) : undefined), [enabled, key])
  const stored = useSyncExternalStore(subscribe, getSnapshot, getStoredApprovalModeServerSnapshot)
  const [picked, setPicked] = useState<SessionApprovalMode | undefined>()
  const selectApprovalMode = useCallback(
    (mode: SessionApprovalMode) => {
      setPicked(mode)
      if (enabled) writeStoredApprovalMode(login, mode)
    },
    [enabled, login],
  )

  return {
    approvalMode: picked ?? stored ?? 'assisted',
    explicitApprovalMode: picked ?? 'assisted',
    selectApprovalMode,
    setApprovalMode: setPicked,
  }
}
